import { createApi } from '@reduxjs/toolkit/query/react';
import { Response } from '../../models/shared';
import { transformResponseData } from '../utils';
import baseQueryWithReauth from './base';
import { CreateReferralDTO, ReferralDTO, UpdateReferralDTO } from '../../types/Referral';

const referralsApi = createApi({
  reducerPath: 'referralsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['referrals'],
  endpoints: (build) => ({
    getReferralById: build.query<Response<ReferralDTO>, { businessId: string, eventId: string, referralId: string }>({
      query: ({ referralId, businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/referrals/${referralId}`,
        method: 'GET',
      }),
    }),
    getAllReferrals: build.query<(ReferralDTO[] | null), { businessId: string, eventId: string }>({
      providesTags: ['referrals'],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/referrals`,
        method: 'GET',
      }),
      transformResponse: (response: Response<ReferralDTO[]>): (ReferralDTO[] | null) => response.data || null,
    }),
    createReferral: build.mutation<Response<ReferralDTO>, { referral: CreateReferralDTO, businessId: string, eventId: string, }>({
      query: ({
        referral, businessId, eventId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/referrals`,
        method: 'POST',
        body: referral,
      }),
      invalidatesTags: ['referrals'],
    }),
    updateReferral: build.mutation<Response<ReferralDTO>, { referral: UpdateReferralDTO, businessId: string, eventId: string }>({
      query: ({
        referral, businessId, eventId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/referrals`,
        method: 'PUT',
        body: referral,
      }),
      invalidatesTags: ['referrals'],
    }),
    deleteReferral: build.mutation<ReferralDTO | null, { businessId: string, eventId: string, referralId: string }>({
      query: ({
        referralId, businessId, eventId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/referrals/${referralId}`,
        method: 'DELETE',
      }),
      transformResponse: transformResponseData,
      invalidatesTags: ['referrals'],
    }),
    getReferralNameByCode: build.query<{ name: string | null, show_heads_up: boolean }, { code: string, eventId: string }>({
      query: ({ code, eventId }) => ({
        url: `/events/${eventId}/referrals/code/${code}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<{ name: string | null, show_heads_up: boolean }>): ({ name: string | null, show_heads_up: boolean }) => response.data || { name: null, show_heads_up: false },
    }),
  }),
});

export const {
  useGetReferralByIdQuery, useGetAllReferralsQuery, useCreateReferralMutation, useUpdateReferralMutation, useDeleteReferralMutation, useGetReferralNameByCodeQuery,
} = referralsApi;
export { referralsApi };
